import { Node, mergeAttributes } from '@tiptap/core'
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react'

import {
  DiffMarkerBox,
  PARAGRAPH_SEPARATOR,
  SLOT_TAGS,
  diffMarkerWrapperClass,
  type DiffMarkerVariant,
} from './diff-marker'

const readLabel = (node: { attrs?: Record<string, unknown> | null }): string =>
  typeof node.attrs?.label === 'string' ? node.attrs.label : ''

// Expand the paragraph sentinel back to blank lines for serialization.
const restoreParagraphs = (text: string): string => text.split(PARAGRAPH_SEPARATOR).join('\n\n')

const SLOT_SELECTOR = SLOT_TAGS.join(', ')

const elementHasSlot = (el: HTMLElement): boolean =>
  el.matches(SLOT_SELECTOR) || el.querySelector(SLOT_SELECTOR) != null

interface DiffMarkerConfig {
  name: string
  /** Canonical tag, emitted by renderHTML/renderMarkdown and parsed. */
  tag: string
  // Extra tags that parse to this node but are never emitted. Any tag the
  // backend nests here must be listed: @tiptap/markdown renders the whole block
  // as literal text if it holds a tag no extension's parseHTML declares.
  aliasTags?: Array<string>
  variant: DiffMarkerVariant
}

function createDiffMarker(config: DiffMarkerConfig) {
  const { name, tag, aliasTags = [], variant } = config

  function DiffMarkerNodeView({ node }: NodeViewProps) {
    return (
      <NodeViewWrapper as="div" contentEditable={false} className={diffMarkerWrapperClass}>
        <DiffMarkerBox variant={variant} label={readLabel(node)} hostsSlot={node.attrs.hasSlot === true} />
      </NodeViewWrapper>
    )
  }

  return Node.create({
    name,
    group: 'block',
    atom: true,
    selectable: false,

    addAttributes() {
      return {
        label: {
          default: '',
          // Trim the newlines that wrap a block-parsed marker's inner text.
          parseHTML: (el) => (el.textContent ?? '').trim(),
          renderHTML: () => ({}),
        },
        hasSlot: {
          default: false,
          parseHTML: (el) => elementHasSlot(el),
          renderHTML: () => ({}),
        },
      }
    },

    parseHTML() {
      return [tag, ...aliasTags].map((t) => ({ tag: t }))
    },

    renderHTML({ node, HTMLAttributes }) {
      return [tag, mergeAttributes(HTMLAttributes), restoreParagraphs(readLabel(node))]
    },

    renderMarkdown(node) {
      return `\n<${tag}>${restoreParagraphs(readLabel(node))}</${tag}>\n`
    },

    addNodeView() {
      return ReactNodeViewRenderer(DiffMarkerNodeView)
    },
  })
}

// Green region where the action's content renders, injected via context. The
// `content` alias is the empty slot the backend nests inside; it must be
// registered or the markdown parser drops the whole block to literal text.
export const AddedContent = createDiffMarker({
  name: 'addedContent',
  tag: 'added_content',
  aliasTags: ['content', 'insertion_point'],
  variant: 'added',
})

// Red region whose inner text is the removed content.
export const RemovedContent = createDiffMarker({
  name: 'removedContent',
  tag: 'removed_content',
  aliasTags: ['deletion_point'],
  variant: 'removed',
})
